import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Button,
  Typography,
  Box,
  Avatar,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Skeleton,
} from "@mui/material";
import * as Yup from "yup";
import EventNoteIcon from "../assets/images/notetitle.png";
import CloseIcon from "../assets/images/closeIcon.png";
import Select from "react-select";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { CreateNote, GetNoteDetail, UpdateNote } from "../service/NotesService";
import { PreferencesContext } from "../PreferenceContext";
import { AlertType } from "../shared/AlertType";
import { GetTempTypeList } from "../service/TemperatureTypeService";
import DatePickers from "../assets/images/DatePicker.png";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

interface AddNoteDialogProps {
  open: boolean;
  UID: number;
  STAKEUID: number;
  onClose: (CODE?: string) => void;
}

const AddNoteDialog: React.FC<AddNoteDialogProps> = ({
  open,
  UID,
  STAKEUID,
  onClose,
}) => {
  interface INote {
    UID: number;
    notes: string;
    date: Dayjs | null;
    temperature: string | null;
    temperatureColor: string | null;
    stakeUid: number | null;
  }

  const initNote: INote = {
    UID: 0,
    notes: "",
    // date: null,
    date: dayjs(),
    temperature: null,
    temperatureColor: null,
    stakeUid: null,
  };

  const { userContext, updateUserContext } = useContext(PreferencesContext);

  const [temperatureTypeColl, setTemperatureTypeColl] = useState<any[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const notesSchema = Yup.object().shape({
    UID: Yup.number().nullable(),
    notes: Yup.string()
      .transform((value: string | undefined | null) => {
        // Convert empty or whitespace-only strings to null
        if (typeof value === "string" && value.trim() === "") {
          return null;
        }
        return value;
      })
      .nullable()
      .required("Latest notes is a required")
      .max(5000, "Notes must be at most 5000 characters"),
    date: Yup.mixed<Dayjs>().nullable().required("Date is required"),
    temperature: Yup.string().nullable(),
    temperatureColor: Yup.string().nullable(),
    stakeUid: Yup.number().nullable(),
  });
  const methods = useForm({
    resolver: yupResolver(notesSchema) as any,
    defaultValues: initNote,
  });

  const handleSubmit = async (formValues: any) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      let response: any;
      // const formattedDate = formValues.date
      //   ? formValues.date.toISOString()
      //   : null;
      const isUpdate = Number(methods.getValues("UID")) > 0;
      const payload = {
        stakeUid: STAKEUID || 0,
        notesText: formValues.notes,
        notesDate: formValues.date,
        temperature: formValues.temperature,
        temperatureColor: formValues.temperatureColor,
        UID: UID || 0,
      };
      response = isUpdate
        ? await UpdateNote(payload)
        : await CreateNote(payload);
      if (response?.data) {
        onClose("SUBMIT");
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: response.message,
          alertType: AlertType.Success,
        });
      }
    } catch (error: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: error.message,
        alertType: AlertType.Error,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getNoteDetail = async (UID: number) => {
    setLoading(true);
    try {
      let response: any;
      let params = new URLSearchParams();
      params.append("UID", UID?.toString());
      response = await GetNoteDetail(params);
      if (response?.data?.data) {
        const note = response?.data?.data;
        methods.reset(
          {
            ...initNote,
            UID: note.UID,
            temperature: note.TEMPERATURE_TYPE || "",
            notes: note.NOTES_TEXT || "",
            date: note.NOTES_DATE ? dayjs(note.NOTES_DATE) : null,
            stakeUid: note.STAKE_UID || "",
          },
          {
            keepErrors: true,
          }
        );
      }
    } catch (error: any) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: error.message,
        alertType: AlertType.Error,
      });
    } finally {
      setLoading(false);
    }
  };
  const fetchTemperatureTypeList = async () => {
    try {
      let params = new URLSearchParams();
      const response: any = await GetTempTypeList(params);

      if (response?.data) {
        setTemperatureTypeColl(response?.data);
        return response?.data;
      }
    } catch (error) {
      console.error("Error fetching list:", error);
    }
  };
  useEffect(() => {
    if (UID > 0) {
      getNoteDetail(UID);
    } else {
      methods.reset(initNote);
    }
    fetchTemperatureTypeList();
  }, [UID, STAKEUID]);

  const handleClose = () => onClose();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          padding: "30px",
          margin: 0,
          maxWidth: "738px",
        },
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              padding: "0px  0px  16px 0px ",
            }}
            className="label-item"
          >
            <Avatar sx={{ mr: 2, width: "42px", height: "42px" }}>
              <img
                src={EventNoteIcon}
                alt="Edit profile"
                style={{ width: "42px", height: "42px" }}
              />
            </Avatar>
            <Typography className="edit-popup-title">
              {UID > 0 ? "Edit" : "Add"} notes
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                ml: "auto",
                padding: "0 !important",
                backgroundColor: "transparent !important",
                "&:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
            >
              <img
                src={CloseIcon}
                alt="Close"
                style={{ width: 24, height: 24 }}
              />
            </IconButton>
          </DialogTitle>
          {isLoading ? (
            <Skeleton
              variant="rectangular"
              animation="wave"
              width="100%"
              height={120}
            />
          ) : (
            <DialogContent
              dividers
              sx={{
                px: 3,
                py: 2,
                padding: "16px 0px 0px 0px",
                borderBottom: "none",
              }}
            >
              <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    width: "148px",
                    textAlign: "left",
                    paddingRight: "12px",
                    paddingTop: "14px",
                  }}
                  className=" label-item-add-stake"
                >
                  * Date
                </Typography>
                <Controller
                  name="date"
                  control={methods.control}
                  render={({ field }) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        format="DD-MM-YYYY"
                        value={field.value ? dayjs(field.value) : null}
                        onChange={(newDate) => {
                          field.onChange(
                            newDate ? dayjs(newDate).format("YYYY-MM-DD") : null
                          );
                        }}
                        slots={{
                          openPickerButton: (props) => (
                            <IconButton
                              {...props}
                              disableRipple
                              sx={{
                                p: 0,
                                "&:hover": {
                                  backgroundColor: "transparent",
                                },
                              }}
                            >
                              <img src={DatePickers} alt="calendar" />
                            </IconButton>
                          ),
                        }}
                        slotProps={{
                          textField: {
                            error: !!methods.formState.errors.date,
                            helperText: methods.formState.errors.date?.message,
                            sx: {
                              fontSize: "16px",
                              minHeight: "52px",
                              width: "555px",
                              backgroundColor: "white",
                              "& .MuiInputBase-input": {
                                paddingLeft: "16px",
                                paddingRight: "16px",
                                height: "52px",
                                boxSizing: "border-box",
                              },
                              "& .MuiFormHelperText-root": {
                                marginLeft: 0,
                                marginRight: 0,
                                p: 0,
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  )}
                />
              </Box>
              {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Note Date"
              format="DD-MM-YYYY"
              value={note.date}
              onChange={(newDate) => setNote({ ...note, date: newDate })}
              slotProps={{
                textField: {
                  sx: {
                    fontSize: "16px",
                    height: "52px",
                    width: "555px",
                    backgroundColor: "white",
                    "& .MuiInputBase-input": {
                      paddingLeft: "16px",
                      paddingRight: "16px",
                      height: "52px",
                      boxSizing: "border-box"
                    }
                  }
                }
              }}
            />
          </LocalizationProvider> */}
              <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    width: "148px",
                    textAlign: "left",
                    paddingRight: "12px",
                  }}
                  className="label-item-add-stake"
                >
                  Latest Status
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <Controller
                    name="temperature"
                    control={methods.control}
                    render={({ field }) => {
                      const selectedOption = temperatureTypeColl.find(
                        (option) => option.value === field.value
                      );
                      return (
                        <Select
                          options={temperatureTypeColl}
                          value={selectedOption}
                          // onChange={(option) =>
                          //   field.onChange(option?.value || "")
                          // }
                          onChange={(option) => {
                            field.onChange(option?.value || "");
                            methods.setValue(
                              "temperatureColor",
                              option?.COLOUR_HASH_CODE || ""
                            );
                          }}
                          menuPortalTarget={document.body}
                          isClearable
                          isSearchable
                          placeholder="Select Status"
                          styles={{
                            control: (base) => ({
                              ...base,
                              height: "52px",
                              width: "535px",
                              backgroundColor:
                                selectedOption?.COLOUR_HASH_CODE || "white",
                              borderColor: "rgba(0, 0, 0, 0.23)",
                              "&:hover": {
                                borderColor: "rgba(0, 0, 0, 0.87)",
                              },
                              boxShadow: "none",
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: "2px 8px",
                            }),
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: 0,
                              fontWeight: 400,
                              fontSize: "14px",
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isSelected
                                ? state.data.COLOUR_HASH_CODE
                                : state.isFocused
                                ? "#eee"
                                : "white",
                              color: "#000",
                            }),
                          }}
                        />
                      );
                    }}
                  />
                </Box>
              </Box>
              <Box display="flex" alignItems="flex-start" sx={{ mb: 2 }}>
                <FormLabel
                  sx={{
                    width: "148px",
                    textAlign: "left",
                    paddingRight: "12px",
                    paddingTop: "14px",
                  }}
                  className={`label-item-add-stake ${
                    methods.formState.errors.notes ? "validation-message" : ""
                  }`}
                >
                  * Latest Notes
                </FormLabel>
                <Controller
                  name="notes"
                  control={methods.control}
                  render={({ field }) => {
                    const currentLength = field.value?.length || 0;
                    const maxLength = 5000;
                    return (
                      <Box sx={{ width: "555px" }}>
                        <TextField
                          {...field}
                          multiline
                          rows={4}
                          fullWidth
                          error={!!methods.formState.errors.notes}
                          helperText={methods.formState.errors.notes?.message}
                          // value={note?.notes || ""}
                          // onChange={(e) => setNote({ ...note, notes: e.target?.value || "" })}
                          inputProps={{
                            maxLength: 5000,
                          }}
                          sx={{
                            "& textarea": {
                              height: "150px !important",
                              maxHeight: "150px !important",
                              overflowY: "auto !important",
                              boxSizing: "border-box",
                              resize: "none",
                              pt: 0,
                            },
                            "& .MuiOutlinedInput-root": {
                              alignItems: "flex-start",
                            },
                            "& .MuiFormHelperText-root": {
                              marginLeft: 0,
                              marginRight: 0,
                            },
                          }}
                        />
                        {!methods.formState.errors.notes && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-end",
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "gray" }}
                            >
                              ({currentLength}/{maxLength})
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    );
                  }}
                />
              </Box>
            </DialogContent>
          )}

          <DialogActions
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              padding: 0,
              width: "100%",
              height: "42px",
              paddingTop: "30px",
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              className="label-item-button "
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                borderRadius: "8px",
                borderColor: "#4C0808",
                color: "#4C0808",
                // marginRight: "28px",
                width: "98px",
                height: "42px",
                backgroundColor: "#ffffff",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="label-item-button "
              disabled={isSubmitting}
              sx={{
                boxShadow: "none",
                textTransform: "none",
                fontWeight: "400",
                borderRadius: "8px",
                width: "98px",
                height: "42px",
                backgroundColor: "#4C0808",
                "&:hover": {
                  backgroundColor: "#4C0808",
                  boxShadow: "none",
                },
              }}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddNoteDialog;
